import React, { useState } from 'react';
import styled from 'styled-components';

import { CategoryHighlightBlockData } from 'api/cms';
import { PublicPage } from 'api/website';
import { Box } from 'components/Box';
import { SecondaryButton } from 'components/Button';
import { Divider } from 'components/Divider';
import { RichTextHeadline, Text } from 'components/Text';
import { gutterWidth, transition } from 'theme';

import { CategoryHighlightBlockCategory } from './CategoryHighlightBlockCategory';

const StyledSecondaryButton = styled(SecondaryButton)`
  &:hover,
  &:focus {
    background: white;
  }
`;

type Props = {
  content: PublicPage;
  data: CategoryHighlightBlockData;
  blockIndex: number;
  pageUpdated: string;
};

export function CategoryHighlightBlock({
  content,
  data: { headline, categories },
  blockIndex,
  pageUpdated,
}: Props) {
  const homePageUrl = content.subsite.homePage && content.subsite.homePage.url;
  const [showAll, setShowAll] = useState(false);

  return (
    <Box data-qa-id="cms-category-highlight">
      {headline && (
        <RichTextHeadline textAlign="center">{headline}</RichTextHeadline>
      )}

      <Box
        m={[-gutterWidth / 4, null, -gutterWidth / 2]}
        display="flex"
        flexWrap="wrap"
      >
        {categories.slice(0, 3).map((category, index) => (
          <CategoryHighlightBlockCategory
            category={category}
            homePageUrl={homePageUrl}
            key={`${category.name}-${index}`} // eslint-disable-line react/no-array-index-key
            blockIndex={blockIndex}
            pageUpdated={pageUpdated}
          />
        ))}
      </Box>

      {categories.length > 3 && (
        <>
          {showAll && (
            <Box
              m={[-gutterWidth / 4, null, -gutterWidth / 2]}
              display="flex"
              flexWrap="wrap"
            >
              {categories.slice(3).map((category, index) => (
                <CategoryHighlightBlockCategory
                  category={category} // eslint-disable-next-line react/no-array-index-key
                  key={`${category.name}-${index}`}
                  homePageUrl={homePageUrl}
                  blockIndex={blockIndex}
                  pageUpdated={pageUpdated}
                />
              ))}
            </Box>
          )}
          <Box position="relative">
            <Divider my={gutterWidth * 1.5} />
            <Box
              position="absolute"
              left="50%"
              top="50%"
              transform="translate(-50%, -50%)"
            >
              <StyledSecondaryButton
                onClick={() => setShowAll(!showAll)}
                bg="white !important"
              >
                <Box component="span" mr="8px" whiteSpace="noWrap">
                  {showAll
                    ? getText('Show fewer categories')
                    : getText('Show all categories')}
                </Box>
                <Box
                  transform={showAll ? 'translateY(-9%)' : 'rotate(-90deg)'}
                  transition={`transform ${transition}`}
                  display="inline-block"
                  verticalAlign="middle"
                >
                  <Text styleAs="h4" lineHeight="1">
                    {showAll ? '–' : '+'}
                  </Text>
                </Box>
              </StyledSecondaryButton>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
